import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import {
  Home,
  LogOut,
  User,
  ShoppingBag,
  Calendar,
  MessageSquare,
  MessageCircle
} from 'lucide-react';
import axios from 'axios';
import TrripLogo from '../../assets/Trrip_logo/trrip_logo.png';
import MobileNav from './DashboardComponents/MobileNav';
import ImageGallery from './DashboardComponents/ImageGallery';
import PartnerNavbar from '../../components/Dashboard/PartnerNavbar';

const ShopDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('');
  const [userData, setUserData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const path = location.pathname.split('/shop/')[1] || '';
    setActiveTab(path || 'home');
  }, [location]);

  const fetchUserData = async () => {
    const token = localStorage.getItem('token_partner_shop');
    const id = localStorage.getItem('id_partner_shop');
    if (!token || !id) {
      navigate('/login');
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/auth/profile/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserData(response.data);
      
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const partnerId = tokenPayload.business_id;
        const walletRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/wallet/Shop/${partnerId}`);
        if (walletRes.data.success) {
          setWalletBalance(walletRes.data.balance);
        }
      } catch (err) {
        console.error('Error fetching wallet balance:', err);
      }
    } catch (error) {
      console.error('Error fetching shop data:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token_partner_shop');
    localStorage.removeItem('id_partner_shop');
    localStorage.removeItem('roleid');
    navigate('/login');
  };

  const NavLink = React.memo(({ icon: Icon, label, value, path }) => (
    <button
      onClick={() => {
        navigate(`/shop/${path}`);
      }}
      className={`flex items-center w-full gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 group ${activeTab === value
        ? 'bg-[#067978] text-white shadow-lg shadow-teal-900/20'
        : 'text-gray-500 hover:bg-gray-50 hover:text-[#067978]'
        }`}
    >
      <Icon size={20} className={`${activeTab === value ? 'text-white' : 'text-gray-400 group-hover:text-[#067978]'} transition-colors`} />
      <span>{label}</span>
    </button>
  ));

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#F8FAFC] flex flex-col font-sans">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Modern Partner Header Navbar */}
      <PartnerNavbar
        portalType="shop"
        subtitle="Retail Shop Management"
        userData={userData}
        walletBalance={walletBalance}
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden lg:flex w-64 bg-white border-r border-gray-100 flex-col flex-shrink-0 overflow-hidden select-none shadow-[4px_0_24px_rgba(0,0,0,0.02)]">
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] mb-4 pl-4">Main Menu</p>
            <NavLink icon={Home} label="Dashboard" value="home" path="" />
            <NavLink icon={User} label="Shop Profile" value="profile" path="profile" />
            <NavLink icon={ShoppingBag} label="Shop Products" value="listing" path="listing" />
            <NavLink icon={Calendar} label="Shop Orders" value="booking" path="booking" />
            <NavLink icon={MessageSquare} label="Customer Reviews" value="reviews" path="reviews" />
            <NavLink icon={MessageCircle} label="Support" value="support" path="support" />
          </nav>
          <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-white">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl transition-all group"
            >
              <LogOut size={18} className="text-rose-500 group-hover:scale-110 transition-transform" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <MobileNav
          portalType="shop"
          subtitle="Retail Shop Management"
          activeTab={activeTab}
          navigate={(path) => navigate(path ? `/shop/${path}` : '/shop')}
          userData={userData}
          handleLogout={handleLogout}
          isMenuOpen={isMobileMenuOpen}
          setIsMenuOpen={setIsMobileMenuOpen}
        />

        <div id="dashboard-scroll-container" className="flex-1 overflow-y-auto bg-[#F8FAFC]">
          <div className="max-w-[1920px] mx-auto min-h-full px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 lg:pb-8">
            <Outlet context={{ userData, setUserData, setIsGalleryOpen, setGalleryIndex }} />
          </div>
        </div>

        <ImageGallery 
          isOpen={isGalleryOpen} 
          onClose={() => setIsGalleryOpen(false)} 
          images={userData?.image_url || []} 
          initialIndex={galleryIndex} 
        />
      </div>
    </div>
  );
};

export default ShopDashboard;
